<template>
  <section id="home" class="page active">
    <div class="hero">
      <div class="hero-art">
        <video
          class="hero-video"
          src="@/assets/image/showreel.mp4"
          autoplay
          muted
          loop
          playsinline
          preload="metadata"
          aria-label="蝴蝶影视30秒品牌宣传片"
        ></video>
        <i class="gp" style="--x: 62%; --y: 24%; --d: 5.2s; --delay: -1.2s"></i>
        <i class="gp" style="--x: 78%; --y: 38%; --d: 6.1s; --delay: -3.4s"></i>
        <i class="gp" style="--x: 56%; --y: 55%; --d: 4.6s; --delay: -0.8s"></i>
        <i class="gp" style="--x: 84%; --y: 60%; --d: 5.8s; --delay: -2.6s"></i>
        <i class="gp" style="--x: 70%; --y: 76%; --d: 5s; --delay: -4.2s"></i>
        <i class="gp" style="--x: 88%; --y: 26%; --d: 6.4s; --delay: -5s"></i>
      </div>
      <div class="wrap hero-inner">
        <div class="eyebrow">{{ t("hero_eyebrow") }}</div>
        <h1 v-html="t('hero_h1')"></h1>
        <p v-html="t('hero_p')"></p>
        <div class="hero-buttons">
          <button type="button" class="btn gold" @click="$emit('explore')">
            {{ t("hero_explore") }} <span>↗</span>
          </button>
          <button type="button" class="btn" @click="$emit('apply')">
            {{ t("hero_launch") }}
          </button>
        </div>
      </div>
      <div class="hero-caption">{{ t("hero_caption") }}</div>
    </div>
    <div class="wrap">
      <div class="stats">
        <div class="stat">
          <label>{{ t("stat1") }}</label>
          <strong>{{ projectCountLabel }}</strong>
          <small>{{ t("stat1u") }}</small>
        </div>
        <div class="stat">
          <label>{{ t("stat2") }}</label>
          <strong>{{ totalSupplyLabel }}</strong>
          <small>{{ t("stat2u") }}</small>
        </div>
        <div class="stat">
          <label>{{ t("stat3") }}</label>
          <strong>{{ stat3Modes }}</strong>
          <small>{{ t("stat3u") }}</small>
        </div>
        <div class="stat">
          <label>{{ t("stat4") }}</label>
          <strong>30<span style="font-size: 18px">%</span></strong>
          <small>{{ t("stat4u") }}</small>
        </div>
      </div>
      <div class="section" id="projects">
        <div class="section-head">
          <div>
            <h2>{{ t("sec_projects") }}</h2>
            <p>{{ t("sec_projects_sub") }}</p>
          </div>
          <div class="filters">
            <button
              v-for="f in filters"
              :key="f.id"
              type="button"
              class="filter"
              :class="{ active: activeFilter === f.id }"
              @click="$emit('filter', f.id)"
            >
              {{ t(f.labelKey) }}
            </button>
          </div>
        </div>
        <div v-if="activeFilter === 'soon'" class="panel empty" v-html="t('empty_soon')"></div>
        <template v-else>
          <div v-if="chainLoading" class="panel empty">{{ t("toast_chain_load") }}</div>
          <div
            v-else-if="!filteredProjects.length"
            class="panel empty"
            v-html="t('empty_projects')"
          ></div>
          <div v-if="!chainLoading && filteredProjects.length" class="project-grid">
            <ProjectShowcaseCard
              v-for="(p, i) in filteredProjects"
              :key="p.id"
              :project="p"
              :index="p.displayIndex != null ? p.displayIndex : i"
              :sold="soldOf(p)"
              :t="t"
              :fmt="fmt"
              @open="$emit('open-project', $event)"
            />
          </div>
        </template>
        <aside class="launch-card">
          <div class="launch-icon">▧</div>
          <h3>{{ t("launch_title") }}</h3>
          <p class="subtext" v-html="t('launch_sub')"></p>
          <div class="subtext" v-html="t('launch_steps')"></div>
          <button type="button" class="btn" @click="$emit('apply')">
            {{ t("launch_btn") }}
          </button>
        </aside>
      </div>
      <div class="feature-row">
        <div class="feature">
          <span class="feature-icon">01</span>
          <div>
            <b>{{ t("feat1") }}</b>
            <p v-html="t('feat1p')"></p>
          </div>
        </div>
        <div class="feature">
          <span class="feature-icon">02</span>
          <div>
            <b>{{ t("feat2") }}</b>
            <p v-html="t('feat2p')"></p>
          </div>
        </div>
        <div class="feature">
          <span class="feature-icon">03</span>
          <div>
            <b>{{ t("feat3") }}</b>
            <p v-html="t('feat3p')"></p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ProjectShowcaseCard from "@/components/film/ProjectShowcaseCard.vue";

const STAT3_MODES = {
  zh: "固定 / 递增",
  en: "Fixed / Step-up",
  de: "Fest / Steigend",
  ko: "고정 / 증가형",
};

export default {
  name: "HomePage",
  components: { ProjectShowcaseCard },
  props: {
    t: { type: Function, required: true },
    fmt: { type: Function, required: true },
    lang: { type: String, default: "zh" },
    chainProjects: { type: Array, default: () => [] },
    chainLoading: { type: Boolean, default: false },
    activeFilter: { type: String, default: "all" },
  },
  data() {
    return {
      filters: [
        { id: "all", labelKey: "filter_all" },
        { id: "live", labelKey: "filter_live" },
        { id: "soon", labelKey: "filter_soon" },
      ],
    };
  },
  computed: {
    filteredProjects() {
      const list = this.chainProjects || [];
      if (this.activeFilter === "live") {
        return list.filter((p) => !p.isFinish && p.sold < p.supply);
      }
      if (this.activeFilter === "all") return list;
      return [];
    },
    projectCountLabel() {
      return String(this.chainProjects.length).padStart(2, "0");
    },
    totalSupplyLabel() {
      const sum = this.chainProjects.reduce((s, p) => s + (Number(p.supply) || 0), 0);
      return sum.toLocaleString("en-US");
    },
    stat3Modes() {
      return STAT3_MODES[this.lang] || STAT3_MODES.en;
    },
  },
  methods: {
    soldOf(project) {
      return Math.max(0, Math.floor(Number(project.sold) || 0));
    },
  },
};
</script>
